import React, {
  forwardRef,
  useImperativeHandle,
  useState,
  ForwardRefRenderFunction,
} from 'react'
import {DateTime} from 'luxon'
import RadioGroup, {RadioGroupProps} from '@mui/material/RadioGroup'
import FormControlLabel from '@mui/material/FormControlLabel'
import styled from 'styled-components'
import ButtomAtom from '../button/button.atom'
import button_date_timeslotStyles from './button_date_timeslot.styles'
import TypographyAtom from '../../to_content/typography/typography.atom'
import StackAtom from '../../to_layout/stack/stack.atom'
import FieldsetAtom from '../fieldset/fieldset.atom'
import SvgIconAtom from '../../to_content/svg_icon/svg_icon.atom'
import {
  BookedSlotDetails,
  DateTimeSlotProps,
} from '../../../props_reusable/visit'
import utils from '../../../utils'

interface ApRadioGroupProps extends RadioGroupProps {
  ap_data_arr: DateTimeSlotProps[]
}

interface ButtonSlotHandle {
  getSlot: () => DateTimeSlotProps
  setDefaultValue: (arg: BookedSlotDetails) => void
}

const apAtomName = {ap_atom_name: 'buttonDateTimeslotAtom'}

interface TimeSlotProps {
  date: string
  time: string
  selected: boolean
}

const TimeSlotLabel = styled((props: TimeSlotProps): JSX.Element => {
  return (
    <StackAtom ap_type="row">
      <div>
        <TypographyAtom
          ahd_type={props.selected ? 'selectedDate' : 'selectableDate'}
        >
          {props.date}
        </TypographyAtom>
      </div>

      <div>
        <TypographyAtom
          ahd_type={props.selected ? 'selectedTime' : 'selectableTime'}
        >
          {props.time}
        </TypographyAtom>
      </div>
    </StackAtom>
  )
})`
  ${button_date_timeslotStyles};
`

const ButtonContainer = styled(props => {
  return <ButtomAtom {...props}>{props.children}</ButtomAtom>
})`
  ${button_date_timeslotStyles};
`

const ButtonDateTimeslotAtom: ForwardRefRenderFunction<
  ButtonSlotHandle,
  ApRadioGroupProps
> = function (props, ref) {
  const [index, setIndex] = useState<number>(0)
  const {ap_data_arr} = props
  const handleSlotChange = (i: number) => {
    setIndex(i)
  }

  const setSlotAsSelected = (slotDetails: BookedSlotDetails) => {
    const matchedIndex = ap_data_arr.findIndex(d => {
      return (
        DateTime.fromISO(slotDetails.visitDate).toFormat('LL/dd/yyyy') ===
          DateTime.fromISO(d.date).toFormat('LL/dd/yyyy') &&
        slotDetails.timeSlotId === d.timeSlotId &&
        slotDetails.timeSlotGroupId === d.timeSlotGroupId
      )
    })
    if (matchedIndex >= 0) setIndex(matchedIndex)
  }

  useImperativeHandle(ref, () => ({
    getSlot: (): DateTimeSlotProps => {
      return ap_data_arr[index]
    },
    setDefaultValue: setSlotAsSelected,
  }))

  const formatDate = (d: string) =>
    utils.formatDate(d, {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
    })

  return (
    <FieldsetAtom>
      <RadioGroup {...apAtomName} name="radio-buttons-group">
        {ap_data_arr.map((slot, i) => (
          <ButtonContainer
            key={`${slot.date}-${slot.timeSlotId}-${slot.startTime}-${slot.endTime}`}
            ap_type={i === index ? 'primary' : 'secondary'}
            onClick={() => handleSlotChange(i)}
          >
            <FormControlLabel
              value={slot.date}
              control={
                i === index ? (
                  <div>
                    <SvgIconAtom ap_type="cardCheckSelected" width="18" />
                  </div>
                ) : (
                  <div>
                    <SvgIconAtom ap_type="cardCheckUnselected" width="18" />
                  </div>
                )
              }
              label={
                <TimeSlotLabel
                  date={formatDate(slot.date)}
                  time={`${slot.startTime} - ${slot.endTime}`}
                  selected={i === index}
                />
              }
            />
          </ButtonContainer>
        ))}
      </RadioGroup>
    </FieldsetAtom>
  )
}
export default forwardRef(ButtonDateTimeslotAtom)
